import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";

const emptySection = () => ({ h2: "", p: "" });

export default function ArticleEditor({ post, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!post) return setForm(null);
    setForm({
      title: post.title || "",
      excerpt: post.excerpt || "",
      meta_title: post.meta_title || "",
      meta_description: post.meta_description || "",
      keywords: post.keywords || "",
      sections: (post.sections || []).map((s) => ({ h2: s.h2, p: s.p.join("\n\n") })),
    });
  }, [post]);

  if (!post || !form) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (i, k, v) =>
    setForm((f) => {
      const sections = [...f.sections];
      sections[i] = { ...sections[i], [k]: v };
      return { ...f, sections };
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        keywords: form.keywords,
        sections: form.sections
          .filter((s) => s.h2.trim())
          .map((s) => ({
            h2: s.h2.trim(),
            p: s.p.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean),
          })),
      };
      await api.patch(`/admin/blog/${post.id}`, payload);
      toast.success("Article mis à jour.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const label = "text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium block mb-1";

  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white"
        data-testid="article-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy">
            Modifier l&apos;article
          </DialogTitle>
          <DialogDescription>
            Relisez et corrigez le contenu avant publication. Vérifiez les faits mentionnés.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div>
            <label className={label}>Titre (H1)</label>
            <Input
              data-testid="edit-title"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
            />
          </div>
          <div>
            <label className={label}>Extrait</label>
            <Textarea
              data-testid="edit-excerpt"
              rows={2}
              value={form.excerpt}
              onChange={(e) => setField("excerpt", e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={label}>Meta title (max 60 car.)</label>
              <Input
                data-testid="edit-meta-title"
                value={form.meta_title}
                onChange={(e) => setField("meta_title", e.target.value)}
              />
              <span className="text-xs text-slate-400">{form.meta_title.length}/60</span>
            </div>
            <div>
              <label className={label}>Mots-clés</label>
              <Input
                data-testid="edit-keywords"
                value={form.keywords}
                onChange={(e) => setField("keywords", e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className={label}>Meta description (max 155 car.)</label>
            <Textarea
              data-testid="edit-meta-description"
              rows={2}
              value={form.meta_description}
              onChange={(e) => setField("meta_description", e.target.value)}
            />
            <span className="text-xs text-slate-400">{form.meta_description.length}/155</span>
          </div>

          <div>
            <label className={label}>Sections (H2 + paragraphes)</label>
            <div className="space-y-4">
              {form.sections.map((s, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-brand-line p-4 space-y-3"
                  data-testid={`edit-section-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <Input
                      value={s.h2}
                      placeholder="Titre de section (H2)"
                      onChange={(e) => setSection(i, "h2", e.target.value)}
                    />
                    <button
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          sections: f.sections.filter((_, j) => j !== i),
                        }))
                      }
                      className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                      aria-label="Supprimer la section"
                      data-testid={`remove-section-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <Textarea
                    rows={5}
                    value={s.p}
                    placeholder="Paragraphes (séparés par une ligne vide)"
                    onChange={(e) => setSection(i, "p", e.target.value)}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={() =>
                setForm((f) => ({ ...f, sections: [...f.sections, emptySection()] }))
              }
              data-testid="add-section-btn"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-golddark hover:text-brand-navy transition-colors"
            >
              <Plus className="w-4 h-4" /> Ajouter une section
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="cancel-edit-btn">
              Annuler
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-article-btn"
              className="bg-brand-navy hover:bg-brand-navy2 text-white"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConfirm({ post, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (!post) return null;
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/blog/${post.id}`);
      toast.success("Article supprimé.");
      onDeleted();
      onClose();
    } catch (e) {
      toast.error("Échec de la suppression.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="article-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-500" /> Supprimer l&apos;article ?
          </DialogTitle>
          <DialogDescription>
            « {post.title} » sera définitivement supprimé. Cette action est irréversible.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="cancel-delete-btn">
            Annuler
          </Button>
          <Button
            onClick={remove}
            disabled={busy}
            data-testid="confirm-delete-btn"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {busy ? "Suppression…" : "Supprimer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
